// MARK://dhk8/admin-shell/pages/modules/FiscalPage
// Módulo 5 — Ajustes fiscales (RFC, CSD, folios, régimen) + ajustes del negocio
// (horarios, plantilla de recibo) → dhk8-comp-tenant-config + dhk8-fiscal-cfdi (REST + mock).
import { useState } from 'react'
import { Save, UploadCloud } from 'lucide-react'
import * as fiscal from '../../lib/clients/fiscal.js'
import type { FiscalSettings } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'

export default function FiscalPage() {
  const { data, loading, error, reload, setData } = useAsync<FiscalSettings>((s) => fiscal.getFiscalSettings(s))
  const [saving, setSaving] = useState(false)
  const [csdOpen, setCsdOpen] = useState(false)
  // El sheet del CSD ahora maneja archivos REALES (File), no nombres en texto.
  const [csd, setCsd] = useState<{ cer: File | null; key: File | null; pwd: string }>({ cer: null, key: null, pwd: '' })
  const [csdUploading, setCsdUploading] = useState(false)
  const [csdError, setCsdError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  function patch(p: Partial<FiscalSettings>) { if (data) setData({ ...data, ...p }) }
  async function save() { if (!data) return; setSaving(true); try { await fiscal.saveFiscalSettings(data); setSavedAt(Date.now()) } finally { setSaving(false) } }
  async function uploadCsd() {
    if (!csd.cer || !csd.key || !csd.pwd) {
      setCsdError('Selecciona el archivo .cer, el .key y escribe la contraseña.')
      return
    }
    setCsdError(null)
    setCsdUploading(true)
    try {
      const r = await fiscal.uploadCsd(csd.cer, csd.key, csd.pwd)
      setCsdOpen(false)
      setCsd({ cer: null, key: null, pwd: '' })
      patch({ csdUploaded: true, csdExpiresAt: r.expiresAt })
    } catch (e) {
      setCsdError(e instanceof Error ? e.message : 'No se pudo subir el CSD.')
    } finally {
      setCsdUploading(false)
    }
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">Ajustes fiscales</h1><p className="adm-page__sub">Datos fiscales del negocio, CSD, folios y plantilla de recibo</p></div>
        <div className="adm-page__actions"><button className="adm-btn adm-btn--primary" onClick={save} disabled={saving || !data}><Save size={16} /> Guardar</button></div>
      </div>
      {savedAt && <div className="adm-banner adm-banner--info" style={{ background: 'var(--pos-success-bg)', color: 'var(--pos-success)' }}>Cambios guardados.</div>}
      {asyncState(loading, error, reload)}
      {data && (
        <div className="adm-grid adm-grid--wide" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
          <div className="adm-card">
            <h2 className="adm-card__title">Datos fiscales</h2>
            <label className="adm-field"><span className="adm-field__label">RFC</span><input className="adm-input" value={data.rfc} onChange={(e) => patch({ rfc: e.target.value.toUpperCase() })} /></label>
            <label className="adm-field" style={{ marginTop: 8 }}><span className="adm-field__label">Razón social</span><input className="adm-input" value={data.legalName} onChange={(e) => patch({ legalName: e.target.value })} /></label>
            <label className="adm-field" style={{ marginTop: 8 }}><span className="adm-field__label">Régimen fiscal</span>
              <select className="adm-select" value={data.regimeCode} onChange={(e) => { const r = fiscal.REGIMES.find((x) => x.code === e.target.value); patch({ regimeCode: e.target.value, regimeName: r?.name ?? '' }) }}>{fiscal.REGIMES.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</select></label>
          </div>
          <div className="adm-card">
            <h2 className="adm-card__title">Certificado de Sello Digital (CSD)</h2>
            <p className="adm-row__meta" style={{ marginBottom: 12 }}>{data.csdUploaded ? <>CSD cargado · vence {data.csdExpiresAt}</> : 'No se ha cargado un CSD'}</p>
            <button className="adm-btn" onClick={() => { setCsd({ cer: null, key: null, pwd: '' }); setCsdError(null); setCsdOpen(true) }}><UploadCloud size={16} /> {data.csdUploaded ? 'Reemplazar CSD' : 'Cargar CSD'}</button>
            <h2 className="adm-card__title" style={{ marginTop: 20 }}>Folios</h2>
            <label className="adm-field"><span className="adm-field__label">Serie</span><input className="adm-input" value={data.folioSeries} onChange={(e) => patch({ folioSeries: e.target.value.toUpperCase() })} maxLength={4} /></label>
            <label className="adm-field" style={{ marginTop: 8 }}><span className="adm-field__label">Próximo folio</span><input className="adm-input" type="number" value={data.folioNext} onChange={(e) => patch({ folioNext: Number(e.target.value) })} /></label>
          </div>
          <div className="adm-card">
            <h2 className="adm-card__title">Horarios del negocio</h2>
            <div className="adm-list">
              {data.businessHours.map((bh, i) => (
                <div key={i} className="adm-row" style={{ cursor: 'default', gap: 8 }}>
                  <input className="adm-input adm-btn--sm" style={{ width: 90 }} value={bh.day} onChange={(e) => patch({ businessHours: data.businessHours.map((x, j) => j === i ? { ...x, day: e.target.value } : x) })} />
                  <input className="adm-input adm-btn--sm" type="time" value={bh.open} onChange={(e) => patch({ businessHours: data.businessHours.map((x, j) => j === i ? { ...x, open: e.target.value } : x) })} />
                  <input className="adm-input adm-btn--sm" type="time" value={bh.close} onChange={(e) => patch({ businessHours: data.businessHours.map((x, j) => j === i ? { ...x, close: e.target.value } : x) })} />
                  <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => patch({ businessHours: data.businessHours.filter((_, j) => j !== i) })}>×</button>
                </div>
              ))}
            </div>
            <button className="adm-btn adm-btn--sm" style={{ marginTop: 10 }} onClick={() => patch({ businessHours: [...data.businessHours, { day: 'Día', open: '09:00', close: '21:00' }] })}>+ Agregar horario</button>
          </div>
          <div className="adm-card">
            <h2 className="adm-card__title">Plantilla de recibo</h2>
            <label className="adm-field"><span className="adm-field__label">Encabezado</span><textarea className="adm-textarea" value={data.receiptHeader} onChange={(e) => patch({ receiptHeader: e.target.value })} /></label>
            <label className="adm-field" style={{ marginTop: 8 }}><span className="adm-field__label">Pie de recibo</span><textarea className="adm-textarea" value={data.receiptFooter} onChange={(e) => patch({ receiptFooter: e.target.value })} /></label>
          </div>
        </div>
      )}
      <Sheet
        open={csdOpen}
        title="Cargar CSD"
        onClose={() => setCsdOpen(false)}
        footer={
          <>
            <button className="adm-btn" onClick={() => setCsdOpen(false)} disabled={csdUploading}>Cancelar</button>
            <button
              className="adm-btn adm-btn--primary"
              onClick={uploadCsd}
              disabled={!csd.cer || !csd.key || !csd.pwd || csdUploading}
            >
              {csdUploading ? 'Subiendo…' : 'Cargar'}
            </button>
          </>
        }
      >
        {csdError && <div className="adm-banner adm-banner--danger" role="alert" style={{ marginBottom: 8 }}>{csdError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Archivo .cer (certificado de sello)</span>
          <input
            className="adm-input"
            type="file"
            accept=".cer,application/x-x509-ca-cert,application/pkix-cert"
            onChange={(e) => setCsd({ ...csd, cer: e.target.files?.[0] ?? null })}
          />
          {csd.cer && <small style={{ color: 'var(--pos-fg-tertiary)' }}>{csd.cer.name} · {(csd.cer.size / 1024).toFixed(1)} KB</small>}
        </label>
        <label className="adm-field" style={{ marginTop: 8 }}>
          <span className="adm-field__label">Archivo .key (llave privada)</span>
          <input
            className="adm-input"
            type="file"
            accept=".key,application/octet-stream,application/pkcs8"
            onChange={(e) => setCsd({ ...csd, key: e.target.files?.[0] ?? null })}
          />
          {csd.key && <small style={{ color: 'var(--pos-fg-tertiary)' }}>{csd.key.name} · {(csd.key.size / 1024).toFixed(1)} KB</small>}
        </label>
        <label className="adm-field" style={{ marginTop: 8 }}>
          <span className="adm-field__label">Contraseña de la llave</span>
          <input
            className="adm-input"
            type="password"
            value={csd.pwd}
            onChange={(e) => setCsd({ ...csd, pwd: e.target.value })}
            autoComplete="off"
            placeholder="Contraseña con la que firmaste el CSD"
          />
        </label>
        <p className="adm-row__meta">El .cer y .key viajan en base64 al backend. La llave se guarda envuelta con un secreto del clúster; la contraseña queda cifrada también. El emisor real (PAC) usa el CSD que registraste con ellos.</p>
      </Sheet>
    </div>
  )
}
